import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { Envelope, MachineState, Reading, StatusLevel } from '@/types';

// Live -> offline threshold.
//
// History: started at 60s × 2, dropped to 10s × 1 when bridges published
// at 5fps, then 30s × 1 for SOURCE-row-time stamping (batch holds lag
// wall-clock 10–15s). Now 60s: source-clock skew on some edge PCs plus
// batch holds pushed real machines past 30s and flapped them offline.
// The card should read offline only after a full minute of true silence.
const STALE_MS      = 60_000;
const STALE_STRIKES = 1;

// Phase C bumps these from {200, 500} so the live store can hold roughly
// the agent2 8h JetStream replay's depth at the 5 msg/s typical rate.
// 5000 = ~16 min. The full 30k/(machine,metric) ring buffer with O(log n)
// chart slicing (plan v2 §6.1) requires co-rewriting MachineCard +
// HistoryPage chart paths and is deferred to a follow-up phase. The
// existing chart components iterate `m.history` as a flat Reading[] and
// can't sit on a per-metric Float64Array without that rewrite.
const MAX_HISTORY   = 5000;
const MAX_LOGS      = 5000;

export interface LogLine {
  ts:           number;
  machine_id:   string;
  company_slug: string;
  /** Optional severity if the log envelope carries one (else 'info'). */
  level:        'debug' | 'info' | 'warn' | 'error' | 'fault';
  message:      string;
  /** Raw payload (kept for display flexibility). */
  raw?:         Record<string, unknown>;
}

function statusFromRaw(raw: unknown): StatusLevel {
  if (raw == null) return 'running';
  const s = String(raw).toUpperCase();
  if (s === 'RUNNING' || s === 'EMITTING' || s === 'MARKING' || s === 'PROCESSING' || s === 'RUN') return 'running';
  if (s === 'READY')                                            return 'ready';
  if (s === 'ERROR' || s === 'FAULT' || s === 'ALARM')          return 'error';
  if (s === 'OFF' || s === 'STATE_OFF')                         return 'off';
  if (s === 'IDLE' || s === 'STANDBY')                          return 'idle';
  return 'running';
}

function deriveLevel(data: Record<string, unknown>): StatusLevel {
  return statusFromRaw(data.Status ?? data.state ?? data.mode);
}

interface MachineStore {
  machines:   Record<string, MachineState>;
  logs:       Record<string, LogLine[]>;
  /** Union of every field name ever seen for a machine, from live SSE OR
   *  historian fetches. Lets the sensor picker stay populated even when a
   *  machine is currently offline. */
  fields:     Record<string, string[]>;
  /** Monotonic clock — bumped every 5s so subscribers can refresh "Xs ago" labels. */
  tick:       number;
  /** Total frames received (any kind). Used for diagnostic frame counter. */
  frameCount: number;
  /** Bumped on every ingestBatch flush so coarse-grained subscribers
   *  (StatusPill, etc.) can react without subscribing to the heavier
   *  s.machines / s.logs records. Also drives the in-process JetStream
   *  warm-resume watermark via s.lastSeenStreamSeq below. */
  version:    number;
  /** High-water JetStream sequence seen across all subjects in this
   *  session. Persisted to IndexedDB by the cache layer so reconnect can
   *  start at lastSeenStreamSeq + 1 instead of 8h replay. */
  lastSeenStreamSeq: number;
  /** Batch ingest — single set() call covers every envelope + log line.
   *  Called by the rAF-flushed NATS ingest queue so a burst of messages
   *  produces one render per animation frame instead of one per frame.
   *  This is the only ingest entry point now that Phase E deleted the
   *  SSE single-row path. */
  ingestBatch: (envs: Envelope[], lines: LogLine[]) => void;
  /** Merge a batch of historic log lines into a machine's slice, deduping
   *  against any live lines already there. Called from useLogHistory. */
  mergeLogs:  (machineKey: string, lines: LogLine[]) => void;
  /** Merge a list of discovered field names for a machine (called from useHistorian). */
  mergeFields: (machineKey: string, fields: Iterable<string>) => void;
  /** Phase D hook — record the highest stream sequence seen so a reconnect
   *  can warm-resume via OptStartSeq instead of replaying 8h. */
  recordStreamSeq: (seq: number) => void;
  reset:      () => void;
  _bumpTick:  () => void;
  _markStale: () => void;
}

const strikes = new Map<string, number>();

export const useMachineStore = create<MachineStore>((set, get) => ({
  machines:   {},
  logs:       {},
  fields:     {},
  tick:       Date.now(),
  frameCount: 0,
  version:    0,
  lastSeenStreamSeq: 0,

  mergeFields: (machineKey, fields) => {
    set((state) => {
      const known  = new Set(state.fields[machineKey] ?? []);
      const before = known.size;
      for (const f of fields) known.add(f);
      if (known.size === before) return state;
      return { fields: { ...state.fields, [machineKey]: [...known].sort() } };
    });
  },

  mergeLogs: (machineKey, lines) => {
    if (lines.length === 0) return;
    set((state) => {
      const cur = state.logs[machineKey] ?? [];
      // Dedup against existing live lines by (ts, message). Historian
      // backfills overlap the live JetStream replay on every page load
      // until Phase D's IndexedDB warm cache lands; without dedup the
      // user would see double-postings of every line that arrived during
      // the seam.
      const seen = new Set<string>();
      for (const l of cur) seen.add(`${l.ts}|${l.message}`);
      const fresh = lines.filter((l) => !seen.has(`${l.ts}|${l.message}`));
      if (fresh.length === 0) return state;
      const merged = [...cur, ...fresh];
      // Newest-first ordering preserved by the rest of the UI; sort once
      // here so the mixed live+backfill stream stays monotonic.
      merged.sort((a, b) => b.ts - a.ts);
      const capped = merged.length > MAX_LOGS ? merged.slice(0, MAX_LOGS) : merged;
      return { logs: { ...state.logs, [machineKey]: capped } };
    });
  },

  ingestBatch: (envs, lines) => {
    if (envs.length === 0 && lines.length === 0) return;
    set((state) => {
      const machines = { ...state.machines };
      const fields   = { ...state.fields };
      const logs     = { ...state.logs };

      // Pre-aggregate per machine so a 1000-envelope flush touching the
      // same machine doesn't pay 1000 array-clones x 5000-cap; we pay
      // ONE merge + cap per machine. Net cost stays linear in flush size.
      const groupedSensor = new Map<string, { reads: Reading[]; latest: Record<string, string | number | null>; level: StatusLevel; ts: number; }>();
      for (const env of envs) {
        const key = `${env.company_slug || 'unknown'}.${env.machine_id || 'unknown'}`;
        const reading: Reading = {
          ts:           env.ts,
          machine_id:   env.machine_id,
          company_slug: env.company_slug,
          subject:      env.subject,
          data:         env.data as Record<string, string | number | null>,
        };
        let g = groupedSensor.get(key);
        if (!g) {
          g = { reads: [], latest: {}, level: 'running', ts: 0 };
          groupedSensor.set(key, g);
        }
        g.reads.push(reading);
        Object.assign(g.latest, env.data as Record<string, string | number | null>);
        if (env.ts > g.ts) {
          g.ts    = env.ts;
          g.level = deriveLevel(env.data);
        }
        const known = new Set(fields[key] ?? []);
        const before = known.size;
        for (const f of Object.keys(env.data)) known.add(f);
        if (before !== known.size) fields[key] = [...known].sort();
      }
      const wallNow = Date.now();
      for (const [key, g] of groupedSensor) {
        strikes.delete(key);
        const existing = machines[key];
        if (existing) {
          // Newest-first ordering: this flush's reads (in arrival order,
          // which on the steady state is monotonic-by-ts) prepended to
          // existing.history, capped. The slice() is the bounded cost.
          const history = [...g.reads.slice().reverse(), ...existing.history];
          // Guard against ts regression. Two paths feed ingestBatch (live
          // tail + IDB cache hydrate on boot); a hydrate-heavy flush has
          // older ts than what's already in the store. Letting ts regress
          // would trip _markStale → flip the card to offline → live cycle.
          // Latest/level/ts only advance forward; history still merges
          // both directions so the chart picks up backfill.
          const newer = g.ts > existing.ts;
          machines[key] = {
            ...existing,
            latest:  newer ? { ...existing.latest, ...g.latest } : existing.latest,
            ts:      newer ? g.ts   : existing.ts,
            level:   newer ? g.level : existing.level,
            count:   existing.count + g.reads.length,
            history: history.length > MAX_HISTORY ? history.slice(0, MAX_HISTORY) : history,
            lastArrivalWallMs: wallNow,
          };
        } else {
          const initialHistory = g.reads.slice().reverse();
          machines[key] = {
            key,
            latest:  g.latest,
            ts:      g.ts,
            count:   g.reads.length,
            level:   g.level,
            history: initialHistory.length > MAX_HISTORY ? initialHistory.slice(0, MAX_HISTORY) : initialHistory,
            lastArrivalWallMs: wallNow,
          };
        }
      }

      for (const line of lines) {
        const key = `${line.company_slug}.${line.machine_id}`;
        const cur = logs[key] ?? [];
        logs[key] = [line, ...cur].slice(0, MAX_LOGS);
      }

      return {
        machines,
        fields,
        logs,
        frameCount: state.frameCount + envs.length + lines.length,
        version:    state.version + 1,
      };
    });
  },

  recordStreamSeq: (seq) => {
    if (seq <= get().lastSeenStreamSeq) return;
    set({ lastSeenStreamSeq: seq });
  },

  reset: () => set({ machines: {}, logs: {}, fields: {}, frameCount: 0, version: 0, lastSeenStreamSeq: 0 }),

  _bumpTick:  () => set({ tick: Date.now() }),

  _markStale: () => {
    const now = Date.now();
    const m   = get().machines;
    let changed = false;
    const next = { ...m };
    for (const k in m) {
      const ms = m[k];
      if (now - ms.ts > STALE_MS) {
        const c = (strikes.get(k) ?? 0) + 1;
        strikes.set(k, c);
        if (c >= STALE_STRIKES && ms.level !== 'offline') {
          next[k] = { ...ms, level: 'offline' };
          changed = true;
        }
      } else if (strikes.has(k)) {
        strikes.delete(k);
      }
    }
    if (changed) set({ machines: next, version: get().version + 1 });
  },
}));

// Background timers (mounted once at module load). _markStale at 5s
// pairs with STALE_MS=10s so the live -> offline transition lands
// inside the 10-15s window the user expects.
setInterval(() => useMachineStore.getState()._bumpTick(),  5_000);
setInterval(() => useMachineStore.getState()._markStale(), 5_000);

/** Subscribe to a single machine slice with shallow equality. */
export function useMachine(key: string): MachineState | undefined {
  return useMachineStore((s) => s.machines[key]);
}

/** All known machine keys, sorted, with shallow eq to avoid re-render churn. */
export function useMachineKeys(): string[] {
  return useMachineStore(useShallow((s) => Object.keys(s.machines).sort()));
}

export function useMachineCount(): { total: number; live: number } {
  return useMachineStore(useShallow((s) => {
    const all = Object.values(s.machines);
    return {
      total: all.length,
      live:  all.filter((m) => m.level !== 'offline').length,
    };
  }));
}

export function useTick(): number {
  return useMachineStore((s) => s.tick);
}

export function useMachineLogs(key: string): LogLine[] {
  return useMachineStore((s) => s.logs[key] ?? EMPTY_LOGS);
}
const EMPTY_LOGS: LogLine[] = [];

export function useFrameCount(): number {
  return useMachineStore((s) => s.frameCount);
}

const EMPTY_FIELDS: string[] = [];
export function useDiscoveredFields(machineKey: string): string[] {
  return useMachineStore((s) => s.fields[machineKey] ?? EMPTY_FIELDS);
}

/** Coarse "did anything change in the live store" signal. Phase D
 *  components needing per-flush re-render hook this rather than
 *  s.machines so they don't pay shallow-eq across the whole record. */
export function useStoreVersion(): number {
  return useMachineStore((s) => s.version);
}
